import {
  FlexCol,
  BodyText,
  BasicContainer,
  FlexRow,
  H3Text,
  H4Text,
  H2Text,
  Loader,
  PrimaryButton,
  BodyTextLarge,
  FlexDynamic,
} from '@minecraft.atoms';
import React, { FC } from 'react';
import { useTranslation, Trans } from '@blocs.i18n';
import Skeleton from 'react-loading-skeleton';
import { WindowWidthContext } from '@minecraft.utils';
import { FEATURE_FLAGS, useFeatureExperiment } from '../../../FeaturesAndExperiments';
import { priceFormatter } from '../../helpers/priceFormatter';
import { TrialTermLink, TrialPlanLegalese, AcceptTrialCheckbox } from '../Legal';
import { CheckoutProps, PaymentDetailFormProps } from '../types';
import { BillingCodes } from '../../enums/plans';
import { PromoCodeInput } from '../PromoCodeInput';

export const PaymentSummarySection: FC<
  PaymentDetailFormProps & {
    selectedPaymentOption?: CheckoutProps['selectedPaymentOption'];
    threeDActionContext?: CheckoutProps['threeDActionContext'];
    uniqueId: string;
    acceptedTrialTerms: boolean;
    setAcceptedTrialTerms: (value: boolean) => void;
    areAllRequiredFieldsFilled: boolean;
    getCompletePurchaseCta: (isTrialPlan: boolean, isFreePlan: boolean) => string;
  }
> = ({
  addPaymentError,
  addPaymentLoading,
  completePurchaseLoading,
  selectedPlan,
  currentPurchase,
  currencyCode,
  countryCode,
  selectedPaymentOption,
  threeDActionContext,
  completePurchaseError,
  uniqueId,
  acceptedTrialTerms,
  setAcceptedTrialTerms,
  areAllRequiredFieldsFilled,
  getCompletePurchaseCta,
  onApplyCoupon,
  couponLoading,
  applyCouponError,
  onRemoveCoupon,
}) => {
  const { t } = useTranslation();
  const registrationPromoCodeFlag = useFeatureExperiment(FEATURE_FLAGS.WEB_REGISTRATION_PROMO_CODE_REDEMPTION);
  const isMobile = WindowWidthContext.useIsMobile();
  const translationCode =
    selectedPlan?.translationCode ?? selectedPlan?.billingCode ?? currentPurchase?.subscription?.plan?.billingCode;
  const appliedCouponCode = currentPurchase?.coupon?.code;
  const isTrialPlan = selectedPlan?.isTrialPeriodEnabled && !appliedCouponCode;
  const isFreePlanSelected = selectedPlan?.billingCode === BillingCodes.registration;
  const isLoading = addPaymentLoading || completePurchaseLoading || couponLoading;

  return (
    <FlexCol w="full" justifyContent="flex-end" gap="2" alignItems="flex-end">
      {addPaymentError && <BodyText color="error">{addPaymentError}</BodyText>}
      <BasicContainer w="full" bgColor="listItem" padding={6} borderRadius="sm">
        {registrationPromoCodeFlag && !isFreePlanSelected && (
          <>
            <PromoCodeInput
              onApplyCoupon={onApplyCoupon}
              onRemoveCoupon={onRemoveCoupon}
              appliedCouponCode={appliedCouponCode}
              applyCouponError={applyCouponError}
              couponLoading={couponLoading}
            />
            <hr />
          </>
        )}
        <Loader loading={isLoading} LoaderComponent={<Skeleton containerClassName="cn_w-full" height="1rem" />}>
          <FlexCol data-qa-id="paywall-payment-summary" w="full" gap="2">
            <FlexRow
              data-qa-id="paywall-membership-summary"
              w="full"
              justifyContent="space-between"
              alignItems="center"
            >
              <FlexCol>
                <BodyTextLarge color="emphasis" fontWeight="semibold">
                  <Trans i18nKey={`common:paywall.success.orderSummary.${translationCode}`} />
                </BodyTextLarge>
              </FlexCol>
              <H3Text color="emphasis" fontWeight="semibold">
                {priceFormatter({
                  price: Number(
                    !appliedCouponCode
                      ? currentPurchase?.total
                      : currentPurchase?.subscription?.plan.price ?? currentPurchase?.total ?? 0
                  ),
                  currencyCode,
                  countryCode,
                  t,
                  noDecimal: !appliedCouponCode,
                })}
              </H3Text>
            </FlexRow>
            {appliedCouponCode && (
              <FlexRow data-testid="coupon-container" w="full" justifyContent="space-between" alignItems="center">
                <FlexCol>
                  <BodyTextLarge color="emphasis" fontWeight="semibold">
                    <Trans
                      i18nKey="common:paywall.summary.promoCodeDiscount"
                      values={{
                        discountPercent: (currentPurchase.discount / currentPurchase?.subscription?.plan?.price) * 100,
                      }}
                    />
                  </BodyTextLarge>
                </FlexCol>
                <H3Text color="emphasis" fontWeight="semibold" data-qa-id="applied-discount">
                  -
                  {priceFormatter({
                    price: Number(currentPurchase?.discount),
                    currencyCode,
                    countryCode,
                    t,
                  })}
                </H3Text>
              </FlexRow>
            )}
          </FlexCol>
        </Loader>
        <hr />
        <Loader loading={isLoading} LoaderComponent={<Skeleton containerClassName="cn_w-full" height="1rem" />}>
          <FlexRow w="full" justifyContent="space-between">
            <H4Text color="emphasis" fontWeight="semibold">
              {t('common:paywall.summary.total')}
            </H4Text>
            <H2Text data-qa-id="payment-total" color="emphasis" as="p">
              {priceFormatter({
                price: currentPurchase?.total ?? 0,
                currencyCode,
                countryCode,
                t,
                noDecimal: !appliedCouponCode,
              })}
            </H2Text>
          </FlexRow>
        </Loader>
      </BasicContainer>
      <FlexRow w="full" justifyContent="flex-end" gap="2">
        <FlexCol gap="1" w="full">
          <FlexDynamic
            mb="8"
            mdProps={{ mb: '0', maxH: '28', className: 'cn_overflow-visible cn_overflow-y-scroll' }}
            w="full"
            justifyContent="flex-end"
            gap="4"
            alignItems="flex-end"
          >
            <BodyText>
              <Trans i18nKey="common:paywall.summary.terms" components={{ a: <TrialTermLink /> }} />
            </BodyText>
            {isTrialPlan && (
              <FlexCol w="full" mb={isLoading || !isMobile ? '2' : '8'}>
                <TrialPlanLegalese
                  billingCode={translationCode}
                  selectedPlan={selectedPlan}
                  countryCode={countryCode}
                />
              </FlexCol>
            )}
          </FlexDynamic>
          {!isLoading && <FlexDynamic w="full" mb="16" mdProps={{ mb: '0' }} />}
          <FlexDynamic
            direction="column"
            mdProps={{ borderPlacement: 'top', className: '', mb: '0' }}
            className="cn_fixed cn_atom_px-4"
            mb="12"
            w="full"
            bottom={-0.5}
            left={0}
            bgOpacity={0.8}
            bgColor="card"
          >
            <Loader
              loading={isLoading || Boolean(threeDActionContext?.token)}
              LoaderComponent={<Skeleton containerClassName="cn_w-full" height="3rem" />}
            >
              {selectedPlan?.acceptTermsCheckboxKey && !appliedCouponCode && !isFreePlanSelected && (
                <AcceptTrialCheckbox
                  text={selectedPlan?.acceptTermsCheckboxKey}
                  checked={acceptedTrialTerms}
                  onChange={setAcceptedTrialTerms}
                  isSticky
                />
              )}
              <FlexCol w="full" my="4">
                <PrimaryButton
                  full
                  label={getCompletePurchaseCta(isTrialPlan, isFreePlanSelected)}
                  disabled={
                    (isTrialPlan && !acceptedTrialTerms) ||
                    (!areAllRequiredFieldsFilled && !selectedPaymentOption) ||
                    isLoading
                  }
                  data-unique-id={uniqueId}
                  data-qa-id="complete-purchase-button"
                  type="submit"
                />
              </FlexCol>
              {completePurchaseError && <BodyText color="error">{completePurchaseError}</BodyText>}
            </Loader>
          </FlexDynamic>
        </FlexCol>
      </FlexRow>
    </FlexCol>
  );
};
